import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, PlugsConnectedIcon as PlugsConnected, MicrosoftOutlookLogoIcon as MicrosoftOutlookLogo, ShieldCheckIcon as ShieldCheck, GlobeIcon as Globe, StorefrontIcon as Storefront, PrinterIcon as Printer, SignInIcon as SignIn, CopyIcon as Copy, BellIcon as Bell, ArrowClockwiseIcon as ArrowClockwise, WifiHighIcon as WifiHigh, DesktopIcon as Desktop, CarIcon as Car, PlusIcon as Plus, TrashIcon as Trash, WrenchIcon as Wrench, BuildingsIcon as Buildings, UsersThreeIcon as UsersThree, ChartBarIcon as ChartBar, ReceiptIcon as Receipt, FileTextIcon as FileText, TicketIcon as Ticket, RobotIcon as Robot, ClipboardTextIcon as ClipboardText } from "@phosphor-icons/react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InterventiConfigSection } from "@/components/InterventiConfigSection";
import { LavorazioniConfigSection } from "@/components/LavorazioniConfigSection";
import { ReportTemplateSection } from "@/components/ReportTemplateSection";
import { ReportSendSection } from "@/components/ReportSendSection";
import { SyncScheduleSection } from "@/components/SyncScheduleSection";
import { AcceptConfigSection } from "@/components/AcceptConfigSection";
import { ContrattiConfigSection } from "@/components/ContrattiConfigSection";
import { AlsoConnectorCard } from "@/components/AlsoConnectorCard";
import { PrintanistaConnectorCard } from "@/components/PrintanistaConnectorCard";
import { DocuSignConnectorCard } from "@/components/DocuSignConnectorCard";
import { ReportisticaSettings } from "@/components/ReportisticaSettings";
import { CopiersConfigSection } from "@/components/CopiersConfigSection";
import { TicketSettingsPanel } from "@/pages/TicketSettings";
import { CrmConfigSection } from "@/components/CrmConfig";
import { ScadenziarioConfigSection } from "@/components/ScadenziarioConfigSection";
import { ServiziConfigSection } from "@/components/ServiziConfigSection";

const SYNC_CAPABLE = new Set(["sophos", "realtimeregister", "zyxel", "nicit", "aruba"]);

function StatusDot({ ok }) {
  return ok
    ? <Badge variant="outline" className="rounded-sm bg-green-100 text-green-800 border-green-200 gap-1"><CheckCircle size={13} weight="fill" /> Connesso</Badge>
    : <Badge variant="outline" className="rounded-sm bg-zinc-100 text-zinc-600 gap-1"><XCircle size={13} /> Non configurato</Badge>;
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [testing, setTesting] = useState("");
  const [syncing, setSyncing] = useState("");
  const [ssoCfg, setSsoCfg] = useState({ redirect_uri: "" });
  const [alertCfg, setAlertCfg] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [veicoli, setVeicoli] = useState([]);
  const [newVeicolo, setNewVeicolo] = useState("");
  const [folder, setFolder] = useState(() => new URLSearchParams(window.location.search).get("folder") || "interventi");
  const [intgTab, setIntgTab] = useState("avvisi");
  const [retention, setRetention] = useState(null);

  const FOLDERS = [
    { key: "interventi", label: "Interventi", icon: Wrench, active: true },
    { key: "lavorazioni", label: "Lavorazioni", icon: Wrench, active: true },
    { key: "ticket", label: "Ticket / Helpdesk", icon: Ticket, active: true },
    { key: "integrazioni", label: "Integrazioni API", icon: PlugsConnected, active: true },
    { key: "amministrazione", label: "Amministrazione", icon: Buildings, active: true, children: [
      { key: "contratti", label: "Contratti", icon: FileText, active: true },
    ] },
    { key: "dispositivi", label: "Dispositivi", icon: Desktop, active: true, children: [
      { key: "fotocopiatrici", label: "Fotocopiatrici", icon: Printer, active: true },
    ] },
    { key: "reportistica", label: "Reportistica", icon: ChartBar, active: true },
    { key: "commerciale", label: "Commerciale", icon: Receipt, active: true },
    { key: "scadenziario", label: "Scadenziario", icon: ClipboardText, active: true },
    { key: "servizi", label: "Servizi", icon: Globe, active: true },
    { key: "azienda", label: "Azienda", icon: Buildings, active: false },
    { key: "utenti", label: "Utenti e permessi", icon: UsersThree, active: false },
    { key: "fatturazione", label: "Fatturazione", icon: Receipt, active: false },
  ];

  const load = () => api.get("/settings").then(({ data }) => { setSettings(data); setForm({}); });
  useEffect(() => {
    load();
    api.get("/auth/sso/config").then(({ data }) => setSsoCfg(data)).catch(() => {});
    api.get("/alerts/config").then(({ data }) => setAlertCfg(data)).catch(() => {});
    api.get("/audit-logs/retention").then(({ data }) => setRetention(data)).catch(() => {});
    api.get("/veicoli").then(({ data }) => setVeicoli(data)).catch(() => {});
  }, []);


  const saveRetention = async () => {
    try {
      const { data } = await api.put("/audit-logs/retention", { days: parseInt(retention.days, 10) || 30 });
      setRetention(data);
      toast.success(`Conservazione log impostata a ${data.days} giorni`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAlertCfg = async () => {
    try { await api.put("/alerts/config", alertCfg); toast.success("Configurazione avvisi salvata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const scanNow = async () => {
    setScanning(true);
    try { const { data } = await api.post("/alerts/scan"); toast.success(`Scansione completata`, { description: `${data.nuovi} nuovi avvisi generati` }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveVeicoli = async (list) => {
    try { const { data } = await api.put("/veicoli", { veicoli: list }); setVeicoli(data.veicoli); toast.success("Veicoli aggiornati"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addVeicolo = () => {
    const v = newVeicolo.trim();
    if (!v) return;
    if (veicoli.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("Veicolo già presente"); return; }
    saveVeicoli([...veicoli, v]); setNewVeicolo("");
  };
  const removeVeicolo = (v) => saveVeicoli(veicoli.filter((x) => x !== v));

  const save = async () => {
    try { await api.put("/settings", form); toast.success("Impostazioni salvate"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const test = async (provider) => {
    setTesting(provider);
    try {
      // Salva prima le credenziali correnti (come "Esegui ora" nella Sincronizzazione automatica),
      // così il test usa sempre i valori appena inseriti e non quelli salvati in precedenza.
      await api.put("/settings", form);
      const { data } = await api.post(`/settings/test/${provider}`);
      if (data.ok) toast.success("Connessione riuscita", { description: data.message });
      else toast.error("Connessione fallita", { description: data.message });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(""); }
  };

  // Sincronizzazione diretta del singolo provider (stessa logica di "Esegui ora"
  // nella Sincronizzazione automatica). Salva prima le credenziali correnti.
  const syncNow = async (provider) => {
    setSyncing(provider);
    try {
      await api.put("/settings", form);
      const { data } = await api.post(`/sync/${provider}`);
      if (data.configured === false) {
        toast.error("Provider non configurato", { description: data.message });
      } else {
        let det = "Sincronizzazione completata";
        if (data.domains != null) det = `${data.domains} domini · ${data.nuovi} nuovi, ${data.aggiornati} aggiornati`;
        else if (data.domini_it != null) det = `${data.domini_it} domini .it · ${data.aggiornati} scadenze aggiornate`;
        else if (data.tenants != null) det = `${data.tenants} tenant · ${data.nuovi} nuovi, ${data.aggiornati} aggiornati, ${data.collegati} collegati`;
        else if (data.organizzazioni != null) det = `${data.organizzazioni} org. · ${data.nuovi} nuovi, ${data.aggiornati} aggiornati, ${data.collegati} collegati`;
        else if (data.servizi != null) det = `${data.servizi} servizi · ${data.nuovi} nuovi, ${data.aggiornati} aggiornati, ${data.collegati} collegati`;
        toast.success("Sincronizzazione riuscita", { description: det });
      }
      load();
    } catch (e) { toast.error("Sincronizzazione non riuscita", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setSyncing(""); }
  };

  if (!settings) return <InternalLayout><div className="p-12">Caricamento…</div></InternalLayout>;
  const p = settings.providers;

  const Section = ({ icon: Icon, title, desc, configured, provider, fields, extra }) => (
    <Card className="rounded-sm border-border shadow-none p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Icon size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">{title}</h3>
            <p className="text-xs text-muted-foreground">{desc}</p>
          </div>
        </div>
        <StatusDot ok={configured} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.key} className={f.full ? "col-span-2" : ""}>
            <Label className="text-xs uppercase tracking-wider">{f.label}</Label>
            <Input
              data-testid={`set-${f.key}`}
              type={f.secret ? "password" : "text"}
              placeholder={settings[`${f.key}_set`] ? (settings[f.key] || "••••••") : f.placeholder || ""}
              value={form[f.key] ?? ""}
              onChange={set(f.key)}
              className="mt-1 rounded-sm font-mono text-sm"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={save} className="rounded-sm">Salva</Button>
        <Button variant="outline" onClick={() => test(provider)} disabled={testing === provider} className="rounded-sm gap-2">
          <PlugsConnected size={16} /> {testing === provider ? "Verifica..." : "Verifica connessione"}
        </Button>
        {SYNC_CAPABLE.has(provider) && (
          <Button variant="outline" onClick={() => syncNow(provider)} disabled={syncing === provider} className="rounded-sm gap-2 border-primary/40 text-primary hover:bg-primary/5" data-testid={`sync-now-${provider}`}>
            <ArrowClockwise size={16} /> {syncing === provider ? "Sincronizzazione..." : "Sincronizza ora"}
          </Button>
        )}
      </div>
      {extra}
    </Card>
  );

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12 max-w-6xl">
        <PageHeader title="Impostazioni" subtitle="Configurazioni organizzate per aree. Seleziona una cartella dal menu." />

        <div className="flex flex-col md:flex-row gap-8 mt-2">
          {/* Sidebar cartelle */}
          <nav className="md:w-56 shrink-0 space-y-1" data-testid="settings-folders-nav">
            {FOLDERS.map((f) => (
              <div key={f.key}>
                <button
                  disabled={!f.active}
                  onClick={() => { if (!f.active) return; if (f.children) { setFolder(f.children[0].key); } else { setFolder(f.key); } }}
                  data-testid={`settings-folder-${f.key}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors text-left ${
                    (folder === f.key || (f.children && f.children.some((c) => c.key === folder))) ? "bg-secondary text-foreground font-medium" : f.active ? "hover:bg-secondary text-foreground" : "text-muted-foreground/50 cursor-not-allowed"
                  }`}
                >
                  <f.icon size={17} weight={folder === f.key ? "fill" : "regular"} />
                  <span className="flex-1">{f.label}</span>
                  {!f.active && <span className="text-[10px] uppercase tracking-wider opacity-70">presto</span>}
                </button>
                {f.children && (f.children.some((c) => c.key === folder) || true) && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-border pl-2">
                    {f.children.map((c) => (
                      <button
                        key={c.key}
                        onClick={() => setFolder(c.key)}
                        data-testid={`settings-folder-${c.key}`}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm transition-colors text-left ${
                          folder === c.key ? "bg-primary text-primary-foreground font-medium" : "hover:bg-secondary text-muted-foreground"
                        }`}
                      >
                        <c.icon size={15} weight={folder === c.key ? "fill" : "regular"} />
                        <span className="flex-1">{c.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Contenuto cartella */}
          <div className="flex-1 min-w-0">

        {folder === "interventi" && (
          <div data-testid="settings-folder-content-interventi">
            <Tabs defaultValue="config" className="mt-2">
              <TabsList className="rounded-sm flex-wrap h-auto">
                <TabsTrigger value="config" data-testid="tab-int-config"><Wrench size={15} className="mr-1.5" />Configurazione</TabsTrigger>
                <TabsTrigger value="template" data-testid="tab-int-template"><FileText size={15} className="mr-1.5" />Template rapportino</TabsTrigger>
                <TabsTrigger value="invio" data-testid="tab-int-invio"><MicrosoftOutlookLogo size={15} className="mr-1.5" />Invio email</TabsTrigger>
                <TabsTrigger value="veicoli" data-testid="tab-int-veicoli"><Car size={15} className="mr-1.5" />Veicoli</TabsTrigger>
              </TabsList>
              <TabsContent value="config" className="mt-4"><InterventiConfigSection /></TabsContent>
              <TabsContent value="template" className="mt-4"><ReportTemplateSection /></TabsContent>
              <TabsContent value="invio" className="mt-4"><ReportSendSection /></TabsContent>
              <TabsContent value="veicoli" className="mt-4">
                <Card className="rounded-sm border-border shadow-none p-6" data-testid="veicoli-section">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Car size={20} weight="bold" className="text-primary" /></div>
                      <div>
                        <h3 className="font-heading font-semibold">Veicoli aziendali</h3>
                        <p className="text-xs text-muted-foreground">Targhe selezionabili nel rapportino di intervento (solo amministratore)</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <Input value={newVeicolo} onChange={(e) => setNewVeicolo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVeicolo(); } }} placeholder="Es. FG123AB — Fiat Panda" className="rounded-sm" data-testid="new-veicolo-input" />
                    <Button onClick={addVeicolo} className="rounded-sm gap-1 shrink-0" data-testid="add-veicolo-btn"><Plus size={16} /> Aggiungi</Button>
                  </div>
                  {veicoli.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessun veicolo configurato.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="veicoli-list">
                      {veicoli.map((v) => (
                        <span key={v} className="inline-flex items-center gap-2 rounded-sm border border-border bg-secondary/50 px-2.5 py-1 text-sm" data-testid={`veicolo-item-${v}`}>
                          {v}
                          <button onClick={() => removeVeicolo(v)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-veicolo-${v}`}><Trash size={14} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {folder === "lavorazioni" && (
          <div data-testid="settings-folder-content-lavorazioni">
            <LavorazioniConfigSection />
          </div>
        )}

        {folder === "fotocopiatrici" && <CopiersConfigSection />}

        {folder === "contratti" && (
          <div data-testid="settings-folder-content-contratti">
            <ContrattiConfigSection />
          </div>
        )}

        {folder === "ticket" && (
          <div data-testid="settings-folder-content-ticket">
            <TicketSettingsPanel />
          </div>
        )}

        {folder === "commerciale" && (
          <div data-testid="settings-folder-content-commerciale">
            <CrmConfigSection />
          </div>
        )}

        {folder === "scadenziario" && (
          <div data-testid="settings-folder-content-scadenziario">
            <ScadenziarioConfigSection />
          </div>
        )}

        {folder === "servizi" && (
          <div data-testid="settings-folder-content-servizi">
            <ServiziConfigSection />
          </div>
        )}

        {folder === "reportistica" && (
          <div data-testid="settings-folder-content-reportistica">
            <ReportisticaSettings />
          </div>
        )}

        {folder === "integrazioni" && (
          <div className="flex gap-1 flex-wrap border-b border-border mb-6" data-testid="settings-folder-content-integrazioni">
            {[["avvisi", Bell, "Avvisi & Sincronizzazione"], ["connettori", PlugsConnected, "Connettori"], ["mappe", Globe, "Mappe & Login"], ["faq", FileText, "FAQ"]].map(([k, Ic, lbl]) => (
              <button key={k} onClick={() => setIntgTab(k)} data-testid={`tab-intg-${k}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-sm border-b-2 -mb-px transition-colors ${intgTab === k ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Ic size={15} /> {lbl}
              </button>
            ))}
          </div>
        )}

        {folder === "integrazioni" && intgTab === "avvisi" && (
        <div className="space-y-6">
          {alertCfg && (
          <Card className="rounded-sm border-border shadow-none p-6" data-testid="alert-config-section">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Bell size={20} weight="bold" className="text-primary" /></div>
                <div>
                  <h3 className="font-heading font-semibold">Avvisi automatici</h3>
                  <p className="text-xs text-muted-foreground">Notifiche in-app ed email per scadenze contratti/domini/SSL, monte ore esaurito e soglie copie</p>
                </div>
              </div>
              <Button variant="outline" onClick={scanNow} disabled={scanning} className="rounded-sm gap-2" data-testid="scan-alerts-btn">
                <ArrowClockwise size={16} /> {scanning ? "Scansione..." : "Scansiona ora"}
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-xs uppercase tracking-wider">Giorni di preavviso scadenze</Label>
                <Input type="number" min={1} data-testid="alert-giorni" value={alertCfg.alert_giorni_preavviso} onChange={(e) => setAlertCfg((c) => ({ ...c, alert_giorni_preavviso: e.target.value }))} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Soglia saldo basso monte ore (%)</Label>
                <Input type="number" min={1} max={100} data-testid="alert-monteore-soglia" value={alertCfg.alert_monteore_soglia_pct ?? 20} onChange={(e) => setAlertCfg((c) => ({ ...c, alert_monteore_soglia_pct: e.target.value }))} className="mt-1 rounded-sm" />
                <p className="text-[11px] text-muted-foreground mt-1">Avviso quando le ore residue di un contratto prepagato scendono sotto questa percentuale</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Email destinatario avvisi</Label>
                <Input data-testid="alert-email" value={alertCfg.alert_email_to} onChange={(e) => setAlertCfg((c) => ({ ...c, alert_email_to: e.target.value }))} placeholder="avvisi@tecnologieverona.it" className="mt-1 rounded-sm" />
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm p-3">
                <div className="text-sm">Invio email <span className="block text-xs text-muted-foreground">richiede Microsoft 365</span></div>
                <Switch data-testid="alert-email-switch" checked={!!alertCfg.alert_email_enabled} onCheckedChange={(v) => setAlertCfg((c) => ({ ...c, alert_email_enabled: v }))} />
              </div>
            </div>
            <div className="mt-4"><Button onClick={saveAlertCfg} className="rounded-sm" data-testid="save-alert-cfg-btn">Salva avvisi</Button></div>
          </Card>
          )}
          {retention && (
          <Card className="rounded-sm border-border shadow-none p-6" data-testid="audit-retention-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><ClipboardText size={20} weight="bold" className="text-primary" /></div>
              <div>
                <h3 className="font-heading font-semibold">Log operazioni — conservazione</h3>
                <p className="text-xs text-muted-foreground">Numero di giorni per cui vengono mantenuti i log di audit (NIS2 / ISO 27001). I log più vecchi vengono eliminati automaticamente ogni giorno.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-xs uppercase tracking-wider">Giorni di conservazione</Label>
                <Input type="number" min={1} max={3650} data-testid="audit-retention-days" value={retention.days ?? 30}
                       onChange={(e) => setRetention((c) => ({ ...c, days: e.target.value }))} className="mt-1 rounded-sm" />
                <p className="text-[11px] text-muted-foreground mt-1">Min 1, max 3650 giorni (10 anni).</p>
              </div>
              <div><Button onClick={saveRetention} className="rounded-sm" data-testid="save-retention-btn">Salva conservazione</Button></div>
            </div>
          </Card>
          )}
          <SyncScheduleSection />
          <AcceptConfigSection />
        </div>
        )}

        {folder === "integrazioni" && intgTab === "connettori" && (
        <div className="space-y-6">
          <AlsoConnectorCard />
          <Section
            icon={ShieldCheck} title="Sophos Partner Center" provider="sophos"
            desc="Sincronizzazione tenant e licenze Sophos Central" configured={p.sophos}
            fields={[
              { key: "sophos_client_id", label: "Client ID" },
              { key: "sophos_client_secret", label: "Client Secret", secret: true },
            ]}
          />
          <Section
            icon={Globe} title="RealtimeRegister" provider="realtimeregister"
            desc="Domini web e scadenze (reseller)" configured={p.realtimeregister}
            fields={[
              { key: "rtr_api_key", label: "API Key", secret: true },
              { key: "rtr_customer", label: "Customer handle" },
            ]}
          />
          <PrintanistaConnectorCard />
          <DocuSignConnectorCard />
          <Section
            icon={Globe} title="Registro .it (nic.it · EPP)" provider="nicit"
            desc="Verifica e aggiorna le scadenze dei domini .it tramite EPP del Registro (richiede Registrar accreditato)" configured={p.nicit}
            fields={[
              { key: "nicit_host", label: "Host EPP", placeholder: "epp.nic.it", full: true },
              { key: "nicit_username", label: "Username EPP (clID)" },
              { key: "nicit_password", label: "Password EPP", secret: true },
            ]}
          />
          <Section
            icon={WifiHigh} title="Zyxel Nebula (MSP)" provider="zyxel"
            desc="Sincronizzazione organizzazioni e dispositivi Zyxel Nebula (richiede MSP Pack + Pro Pack)" configured={p.zyxel}
            fields={[
              { key: "zyxel_api_key", label: "API Key (Nebula Control Center)", secret: true, full: true },
            ]}
          />
          <Section
            icon={Desktop} title="NinjaOne RMM" provider="ninjaone"
            desc="Sincronizzazione dispositivi gestiti (sola lettura). App API Services · grant Client Credentials · scope Monitoring." configured={p.ninjaone}
            fields={[
              { key: "ninja_client_id", label: "Client ID" },
              { key: "ninja_client_secret", label: "Client Secret", secret: true },
              { key: "ninja_region", label: "Regione (eu · us · us2 · ca · oc)", placeholder: "eu" },
            ]}
          />
          <Section
            icon={Globe} title="Aruba Business" provider="aruba"
            desc="Sincronizzazione domini e servizi (hosting, PEC, cloud) dal pannello Aruba Business (rivenditore)" configured={p.aruba}
            fields={[
              { key: "aruba_api_key", label: "API Key (Authorization-Key)", secret: true, full: true },
              { key: "aruba_username", label: "Username Web API" },
              { key: "aruba_password", label: "Password Web API", secret: true },
              { key: "aruba_access_token", label: "Access Token giornaliero (opzionale, sovrascrive il login)", secret: true, full: true },
            ]}
          />
          <Section
            icon={Robot} title="ChatGPT / OpenAI" provider="openai"
            desc="Assistente AI di TECSuite. Lascia vuoto per usare la chiave universale Emergent; inserisci la tua chiave OpenAI per usare il tuo account e la tua fatturazione." configured={p.openai}
            fields={[
              { key: "openai_api_key", label: "API Key OpenAI (sk-…)", secret: true, full: true, placeholder: "sk-…" },
              { key: "openai_model", label: "Modello (default: gpt-5)", placeholder: "gpt-5" },
            ]}
          />
          <div className="rounded-sm border border-border p-4 flex items-start justify-between gap-4" data-testid="ai-data-access-box">
            <div>
              <div className="flex items-center gap-2">
                <Robot size={18} className="text-primary" />
                <p className="font-medium text-sm">Consenti lettura dati TecSuite all'assistente AI</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                Con OFF l'assistente è una chat generica e non accede al database. Con ON può interrogare i dati del gestionale
                <span className="font-medium"> in sola lettura</span> (clienti, contratti, interventi, programmazione, scadenze, valori economici)
                tramite strumenti controllati. Nessuna azione di scrittura o modifica è mai possibile.
              </p>
            </div>
            <Switch
              data-testid="ai-data-access-switch"
              checked={settings?.ai_data_access === "true"}
              onCheckedChange={async (v) => {
                try { await api.put("/settings", { ai_data_access: v ? "true" : "false" }); toast.success(v ? "Lettura dati TecSuite abilitata" : "Lettura dati TecSuite disabilitata"); load(); }
                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }}
            />
          </div>
        </div>
        )}

        {folder === "integrazioni" && intgTab === "mappe" && (
        <div className="space-y-6">
          <Card className="rounded-sm border-border shadow-none p-6" data-testid="maps-section">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Globe size={20} weight="bold" className="text-primary" /></div>
                <div>
                  <h3 className="font-heading font-semibold">Mappe / Google Maps</h3>
                  <p className="text-xs text-muted-foreground">Geocoding e anteprima mappa per il luogo/sede degli interventi. La API key resta lato server e non viene mai esposta al browser.</p>
                </div>
              </div>
              <StatusDot ok={p.maps} />
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3 mb-3">
              <div>
                <div className="text-sm font-medium">Abilita integrazione mappe</div>
                <div className="text-xs text-muted-foreground">Attiva geocoding e anteprima mappa nella scheda intervento (richiede API key valida)</div>
              </div>
              <Switch
                data-testid="maps-enabled-switch"
                checked={(form.maps_enabled ?? (settings.maps_enabled || "")) !== "false"}
                onCheckedChange={(v) => setForm((f) => ({ ...f, maps_enabled: v ? "true" : "false" }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider">Google Maps API Key</Label>
                <Input data-testid="set-google_maps_api_key" type="password" placeholder={settings.google_maps_api_key_set ? "••••••" : "AIza…"} value={form.google_maps_api_key ?? ""} onChange={set("google_maps_api_key")} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Provider</Label>
                <Input data-testid="set-maps_provider" placeholder={settings.maps_provider || "Google Maps"} value={form.maps_provider ?? ""} onChange={set("maps_provider")} className="mt-1 rounded-sm text-sm" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Abilita <b>Geocoding API</b> e <b>Maps Static API</b> sul progetto Google Cloud e attiva la fatturazione (free tier $200/mese). Senza chiave, il pulsante "Apri in Maps" funziona comunque tramite URL di ricerca.</p>
            <div className="flex gap-2 mt-4"><Button data-testid="save-maps-btn" onClick={save} className="rounded-sm">Salva</Button></div>
          </Card>

          <Card className="rounded-sm border-border shadow-none p-6" data-testid="sso-section">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><SignIn size={20} weight="bold" className="text-primary" /></div>
                <div>
                  <h3 className="font-heading font-semibold">Login SSO Microsoft 365</h3>
                  <p className="text-xs text-muted-foreground">Accesso del personale con account Microsoft 365 (Entra ID, OAuth2/OIDC)</p>
                </div>
              </div>
              <StatusDot ok={p.sso} />
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3 mb-3">
              <div>
                <div className="text-sm font-medium">Abilita accesso SSO</div>
                <div className="text-xs text-muted-foreground">Mostra il pulsante "Accedi con Microsoft 365" nella pagina di login</div>
              </div>
              <Switch
                data-testid="sso-enabled-switch"
                checked={(form.sso_enabled ?? (settings.sso_enabled || "")) === "true"}
                onCheckedChange={(v) => setForm((f) => ({ ...f, sso_enabled: v ? "true" : "false" }))}
              />
            </div>
            <div className="border border-dashed border-border rounded-sm p-3 mb-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Redirect URI (da registrare in Azure)</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-xs bg-secondary px-2 py-1 rounded-sm flex-1 break-all" data-testid="sso-redirect-uri">{ssoCfg.redirect_uri || "—"}</code>
                <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(ssoCfg.redirect_uri || ""); toast.success("Copiato"); }}><Copy size={14} /></Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Directory (tenant) ID</Label>
                <Input data-testid="set-sso_tenant_id" placeholder={settings.sso_tenant_id_set ? (settings.sso_tenant_id || "••••••") : ""} value={form.sso_tenant_id ?? ""} onChange={set("sso_tenant_id")} className="mt-1 rounded-sm font-mono text-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Application (client) ID</Label>
                <Input data-testid="set-sso_client_id" placeholder={settings.sso_client_id_set ? (settings.sso_client_id || "••••••") : ""} value={form.sso_client_id ?? ""} onChange={set("sso_client_id")} className="mt-1 rounded-sm font-mono text-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Client Secret</Label>
                <Input data-testid="set-sso_client_secret" type="password" placeholder={settings.sso_client_secret_set ? "••••••" : ""} value={form.sso_client_secret ?? ""} onChange={set("sso_client_secret")} className="mt-1 rounded-sm font-mono text-sm" /></div>
            </div>
            <div className="flex gap-2 mt-4"><Button data-testid="save-sso-btn" onClick={save} className="rounded-sm">Salva</Button></div>
          </Card>
        </div>
        )}

        {folder === "integrazioni" && intgTab === "faq" && (<>
        <Card className="rounded-sm border-border shadow-none p-6 mt-2">
          <h3 className="font-heading text-lg font-semibold mb-1">FAQ · Come configurare le API</h3>
          <p className="text-sm text-muted-foreground mb-4">Guida passo-passo per ottenere le credenziali di ogni servizio esterno.</p>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="sophos">
              <AccordionTrigger data-testid="faq-sophos">Sophos Partner Center</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-1">
                <p>1. Accedi a <b>Sophos Central Partner</b> → Settings &amp; Policies → <b>API Credentials Management</b>.</p>
                <p>2. Crea una nuova credenziale: otterrai <b>Client ID</b> e <b>Client Secret</b>.</p>
                <p>3. Le API usano OAuth2 client-credentials su id.sophos.com; il sistema recupera automaticamente il token e i tenant.</p>
                <p>4. Inserisci Client ID e Secret qui sopra e verifica la connessione.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="rtr">
              <AccordionTrigger data-testid="faq-rtr">RealtimeRegister (domini)</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-1">
                <p>1. Accedi al <b>RealtimeRegister Control Panel</b> → Account → <b>API access / Tokens</b>.</p>
                <p>2. Genera una <b>API Key</b> e annota il tuo <b>customer handle</b> (nome account).</p>
                <p>3. Inserisci API Key e customer handle, quindi verifica: verranno letti domini e scadenze.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="fmaudit">
              <AccordionTrigger data-testid="faq-fmaudit">Printanista / FMAudit (contatori stampanti)</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-1">
                <p>TecSuite si collega alla <b>Printanista Hub Public API</b> (servizio <b>SOAP</b>): <code>https://mds.fmwebaudit.com/WebServices/PublicAPI.asmx</code> (namespace <code>urn:printanista-v5</code>).</p>
                <p>1. Genera la <b>Public API Key</b> in <b>Administration → Settings → Printanista Hub Integration → Generate Key</b> (visibile una sola volta) oppure richiedila a <b>ECI</b> (support@ecisolutions.com). Va inserita nel campo <b>Public API Key</b> (header <code>X-API-KEY</code>).</p>
                <p>2. Inserisci le credenziali <b>Web API</b> (<b>Username</b> e <b>Password</b>): sono distinte dalla Public API Key e vengono validate dal metodo SOAP <code>Authenticate</code>.</p>
                <p>3. Indica l'<b>URL server</b> (default <code>https://mds.fmwebaudit.com</code>) e, se serve, l'<b>Account ID</b> iniziale (opzionale).</p>
                <p>4. Premi <b>Verifica connessione</b>: il badge "Connesso" appare solo dopo un <code>Authenticate</code> reale riuscito.</p>
                <p className="text-amber-700">Nota: la vecchia integrazione REST <code>/api/devices</code> non è più usata (endpoint inesistente su Printanista).</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="nicit">
              <AccordionTrigger data-testid="faq-nicit">Registro .it (nic.it · EPP)</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-1">
                <p>1. Devi essere un <b>Registrar accreditato</b> presso il Registro .it con credenziali <b>EPP</b> (clID e password).</p>
                <p>2. Host EPP: <b>epp.nic.it</b> (porta 700, TLS). Inserisci Username (clID) e Password EPP.</p>
                <p>3. <b className="text-amber-700">Importante:</b> il Registro .it accetta connessioni EPP solo dagli <b>IP autorizzati</b> nel tuo profilo Registrar. Devi aggiungere l'IP pubblico del server dove gira TECSuite alla lista IP consentiti dal pannello <b>rain.nic.it</b>, altrimenti la connessione verrà rifiutata.</p>
                <p>4. Il pulsante "Sincronizza" verifica via EPP <code>&lt;domain:info&gt;</code> la data di scadenza dei domini <b>.it</b> già presenti in "Servizi & Domini" e la aggiorna con il dato ufficiale del Registro.</p>
                <p className="text-muted-foreground">Nota: EPP non consente l'elenco di tutti i domini; l'import massivo iniziale conviene farlo da RealtimeRegister, mentre nic.it garantisce scadenze .it autorevoli.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="sso">
              <AccordionTrigger data-testid="faq-sso">Login SSO Microsoft 365 (Entra ID)</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-1">
                <p>1. Vai su <b>Microsoft Entra admin center</b> → Identità → <b>App registrations</b> → Nuova registrazione.</p>
                <p>2. In <b>Redirect URI</b> seleziona tipo <b>Web</b> e incolla l'URI mostrato qui sopra (pulsante copia).</p>
                <p>3. Copia <b>Directory (tenant) ID</b> e <b>Application (client) ID</b>.</p>
                <p>4. In <b>Certificates &amp; secrets</b> crea un <b>Client secret</b> e copia il valore.</p>
                <p>5. In <b>API permissions</b> aggiungi <b>Microsoft Graph → Delegated → openid, profile, email, User.Read</b> e concedi il consenso admin.</p>
                <p>6. Inserisci Tenant ID, Client ID e Secret qui sopra, attiva l'interruttore <b>Abilita accesso SSO</b> e salva.</p>
                <p className="text-amber-700">Solo gli utenti con un account TECSuite esistente e la stessa email Microsoft potranno accedere via SSO. L'accesso con email e password resta sempre disponibile.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
        <p className="text-xs text-muted-foreground mt-6">
          Le credenziali vengono salvate in modo sicuro e usate dai connettori. Una volta verificate, i moduli Ticket e Servizi attiveranno la sincronizzazione automatica.
        </p>
        </>)}

          </div>
        </div>
      </div>
    </InternalLayout>
  );
}
